import { Country } from '@sportninja/common/hooks/useCountries'
import { Province, useProvinces } from '@sportninja/common/hooks/useProvinces'
import {
  RegistrationDetails,
  useRegistrationDetails,
} from '@sportninja/common/hooks/useRegistrationDetails'
import { useTypes } from '@sportninja/common/hooks/useTypes'
import { RegistrationTypes } from '@sportninja/common/types/Registration'
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react'

interface RegistrationDetailsModalContextProps {
  scheduleId: string
  registrationId?: string
  countries: Country[]
  children: React.ReactNode
  types: RegistrationTypes | null
}

interface FormData {
  email: string
  firstName: string
  lastName: string
  birthDate: string
  address: string
  zipCode: string
  stateProvince: string | number
  country: string
  city: string
  parentFirstName: string
  parentLastName: string
  parentEmail: string
  parentPhone: string
  emergencyFirstName: string
  emergencyLastName: string
  emergencyPhone: string
  parentRelationship: string
  doctorName: string
  doctorPhone: string
  allergies: string
  medicalConditions: string
  preferredPosition: string
  jerseyNumber: string
  dominantSide: string
  jerseySize: string
  tshirtSize: string
  shortSize: string
  hockeyUSANumber: string
  hockeyCanadaNumber: string
  notes: string
  additionalTeamOfficials: any[]
  fieldErrors: any[]
  customError: Record<string, any>
  emergencyEmail: string
  otherMembershipNumber: string
  image?: any
}

interface RegistrationDetailsModalContextType {
  formData: FormData
  updateFormData: (field: string, value: string | boolean) => void
  loading: boolean
  error: any
  countries: Country[]
  provinces: Province[]
  types: RegistrationTypes | null
  registrationDetails: RegistrationDetails | null
  refetch: (shouldRefresh?: boolean) => void
  positions: any[]
  setCustomError: (field: string, message: string) => void
  setFieldErrors: (field: string) => void
  setCustomErroByField: (field: string, message: string) => void
  clearCustomError: () => void
  clearFieldErrors: () => void
}

const RegistrationDetailsModalContext = createContext<
  RegistrationDetailsModalContextType | undefined
>(undefined)

export const RegistrationDetailsModalProvider: React.FC<
  RegistrationDetailsModalContextProps
> = ({ scheduleId, registrationId, countries, children, types }) => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    address: '',
    zipCode: '',
    stateProvince: '',
    country: '',
    city: '',
    parentFirstName: '',
    parentLastName: '',
    parentEmail: '',
    parentPhone: '',
    emergencyFirstName: '',
    emergencyLastName: '',
    emergencyPhone: '',
    parentRelationship: '',
    doctorName: '',
    doctorPhone: '',
    allergies: '',
    medicalConditions: '',
    preferredPosition: '',
    jerseyNumber: '',
    dominantSide: '',
    jerseySize: '',
    tshirtSize: '',
    shortSize: '',
    hockeyUSANumber: '',
    hockeyCanadaNumber: '',
    notes: '',
    additionalTeamOfficials: [],
    fieldErrors: [],
    customError: {},
    emergencyEmail: '',
    otherMembershipNumber: '',
  })

  const clearCustomError = () => {
    setFormData((prev) => ({
      ...prev,
      customError: {},
    }))
  }

  const clearFieldErrors = () => {
    setFormData((prev) => ({
      ...prev,
      fieldErrors: [],
    }))
  }

  const {
    data: registrationDetails,
    loading,
    error,
    refetch,
  } = useRegistrationDetails(scheduleId, registrationId)

  const selectedCountryId = useMemo(() => {
    return countries.find((c) => c.iso_3166_2 === formData.country)?.id
  }, [formData.country, countries])

  const { provinces } = useProvinces(selectedCountryId)

  const { types: typesData, fetchTypes } = useTypes()

  const positions = useMemo(() => {
    // First, lets find the type for the sport id
    const sportType = typesData?.find(
      (type) => type.sport_id === registrationDetails?.schedule?.sport_id
    )
    // Return empty if no sport type is found
    if (!sportType) {
      return []
    }
    const playerTypes = sportType?.data?.find(
      (type) => type.player_types !== undefined
    )
    return playerTypes?.player_types
  }, [typesData, registrationDetails?.schedule?.sport_id])

  useEffect(() => {
    if (!typesData || typesData?.length === 0) {
      fetchTypes()
    }
  }, [fetchTypes, typesData])

  useEffect(() => {
    if (registrationDetails && registrationDetails?.registration_info) {
      const preferredPosition = registrationDetails?.registration_info
        ?.player_type_id
        ? String(registrationDetails?.registration_info?.player_type_id)
        : registrationDetails?.player?.player_type?.id
        ? String(registrationDetails?.player?.player_type?.id)
        : ''

      setFormData((prev) => ({
        ...prev,
        image: registrationDetails?.player?.image,
        city: registrationDetails?.registration_info?.city || '',
        email: registrationDetails?.registration_info?.email || '',
        firstName: registrationDetails?.registration_info?.name_first || '',
        lastName: registrationDetails?.registration_info?.name_last || '',
        birthDate: registrationDetails?.registration_info?.birth_date || '',
        address: registrationDetails?.registration_info?.address || '',
        country: registrationDetails?.registration_info?.country || '',
        parentFirstName:
          registrationDetails?.registration_info?.parent_first_name || '',
        parentLastName:
          registrationDetails?.registration_info?.parent_last_name || '',
        parentEmail: registrationDetails?.registration_info?.parent_email || '',
        parentPhone:
          registrationDetails?.registration_info?.parent_phone_number || '',
        emergencyFirstName:
          registrationDetails?.registration_info?.emergency_first_name || '',
        emergencyLastName:
          registrationDetails?.registration_info?.emergency_last_name || '',
        emergencyPhone:
          registrationDetails?.registration_info?.emergency_phone_number || '',
        parentRelationship:
          registrationDetails?.registration_info?.emergency_relationship || '',
        doctorName: registrationDetails?.registration_info?.doctor_name || '',
        doctorPhone:
          registrationDetails?.registration_info?.doctor_phone_number || '',
        allergies: registrationDetails.registration_info.known_allergies || '',
        medicalConditions:
          registrationDetails?.registration_info?.medical_conditions || '',
        preferredPosition: preferredPosition,
        jerseyNumber:
          registrationDetails?.registration_info?.preferred_jersey_number || '',
        dominantSide:
          registrationDetails?.registration_info?.preferred_dominant_side || '',
        jerseySize:
          registrationDetails?.registration_info?.preferred_jersey_size || '',
        tshirtSize:
          registrationDetails?.registration_info?.preferred_tshirt_size || '',
        shortSize:
          registrationDetails?.registration_info?.preferred_short_size || '',
        hockeyUSANumber:
          registrationDetails?.registration_info
            ?.preferred_governing_body_number_1 || '',
        hockeyCanadaNumber:
          registrationDetails?.registration_info
            ?.preferred_governing_body_number_2 || '',
        notes: registrationDetails?.registration_info?.notes || '',
        zipCode: registrationDetails?.registration_info?.postal_code || '',
        stateProvince:
          Number(registrationDetails?.registration_info?.province) || '',
        emergencyEmail:
          registrationDetails?.registration_info?.emergency_email || '',
        otherMembershipNumber:
          registrationDetails?.registration_info
            ?.preferred_governing_body_number_1 || '',
      }))
    }
  }, [registrationDetails])

  useEffect(() => {
    if (registrationDetails && registrationDetails?.team_official) {
      setFormData((prev) => ({
        ...prev,
        firstName: registrationDetails?.team_official?.name_first || '',
        lastName: registrationDetails?.team_official?.name_last || '',
      }))
    }
  }, [registrationDetails])

  const updateFormData = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const setCustomError = (field: string, message: string) => {
    setFormData((prev) => ({
      ...prev,
      customError: {
        ...prev.customError,
        [field]: message,
      },
    }))
  }

  const setFieldErrors = (field: string) => {
    setFormData((prev) => ({
      ...prev,
      fieldErrors: [...new Set([...prev.fieldErrors, field])],
    }))
  }

  const setCustomErroByField = (field: string, message: string) => {
    setCustomError(field, message)
    setFieldErrors(field)
  }

  const contextValue = {
    formData,
    updateFormData,
    loading,
    error,
    countries,
    provinces,
    types,
    registrationDetails,
    refetch,
    positions,
    setCustomError,
    setFieldErrors,
    setCustomErroByField,
    clearCustomError,
    clearFieldErrors,
  }

  return (
    <RegistrationDetailsModalContext.Provider value={contextValue}>
      {children}
    </RegistrationDetailsModalContext.Provider>
  )
}

export const useRegistrationDetailsModal = () => {
  const context = useContext(RegistrationDetailsModalContext)
  if (!context) {
    throw new Error(
      'useRegistrationDetailsModal must be used within a RegistrationDetailsModalProvider'
    )
  }
  return context
}
